import {
    CheckCircleFilled,
    CopyOutlined,
    DownloadOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    EyeOutlined,
    LoadingOutlined,
    ReloadOutlined,
    SearchOutlined,
    UnlockOutlined,
} from '@ant-design/icons';
import { Button, Input, Progress, message } from 'antd';
import { Presentation } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get } from '../../../axios/axios';
import {
    QUERY_TYPE_LABEL,
    exportDocument,
    queryOrder,
    rememberOrder,
    rememberedOrders,
} from '../api';
import WritingPayModal from '../StepForm/PreviewStep/PayDialog';
import { copyText, formatYuan } from '../StepForm/PreviewStep/utils';
import styles from './index.module.scss';

/** 还在生成：每 10 秒静默刷新一次 */
const ACTIVE_STATUSES = ['GENERATING', 'PENDING'];
const REFRESH_MS = 10000;

const GENERATION = {
    GENERATING: { text: '生成中', tone: 'active' },
    PENDING: { text: '排队中', tone: 'active' },
    COMPLETE: { text: '已完成', tone: 'done' },
    PARTIAL_FAILURE: { text: '部分内容生成失败', tone: 'warn' },
    STOPPED: { text: '已暂停', tone: 'warn' },
};

const PURCHASE_TEXT = {
    UNPAID: '未支付',
    FULL: '全篇解锁',
    CHAPTER: '单章解锁',
    CHAPTER_TO_FULL: '单章 + 补差价解锁全文',
};

const KIND_TEXT = { MAIN: '正文', ADDON: '附加服务', PPT_CODE: '答辩PPT' };
/** 单章解锁的订单不能在线编辑（编辑器的重写会生成未购买的章节） */
const FULL_UNLOCK = ['FULL', 'CHAPTER_TO_FULL'];
const DOC_STATUS = { GENERATING: '生成中', DONE: '已完成', FAILED: '生成失败' };

const normalize = (value) => String(value || '').trim();

const errorText = (error, fallback) => {
    if (error?.response?.status === 429) return '查询过于频繁，请稍后再试';
    return error?.response?.data?.msg || error?.message || fallback;
};

/** 老订单（/ai/paper/query）：{ paperTitle, list:[{ title, downloadUrl }] }；新版单号（MLW… / MLPG…）老接口里没有，不去查 */
async function queryLegacyOrder(no) {
    if (/^ML(W|PG)/i.test(no)) {
        throw new Error(
            /^MLPG/i.test(no)
                ? '答辩PPT 的付款单号不能在这里查询，请从首页「我的生成记录」或付款后的页面进入答辩PPT'
                : '未找到该订单，请检查输入的凭证',
        );
    }
    const res = await get('/ai/paper/query', { orderNo: no }, { slient: true });
    if (res?.success === false || !res?.data) throw new Error(res?.msg || '未找到该订单，请检查输入的凭证');
    return { ...res.data, list: Array.isArray(res.data.list) ? res.data.list : [] };
}

function StatusPill({ tone, children }) {
    return (
        <span className={styles[`status_${tone}`] || styles.status_muted}>
            {tone === 'active' ? <LoadingOutlined /> : null}
            {tone === 'done' ? <CheckCircleFilled /> : null}
            {tone === 'warn' ? <ExclamationCircleFilled /> : null}
            {children}
        </span>
    );
}

export default function OrderResult() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const urlNo = normalize(params.get('no'));
    const [input, setInput] = useState(urlNo);
    const [order, setOrder] = useState(null);
    const [legacy, setLegacy] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState('');
    const [recent, setRecent] = useState(() => rememberedOrders());
    const [payMode, setPayMode] = useState(null);
    const queryNoRef = useRef('');
    const requestRef = useRef(0);

    const runQuery = useCallback(async (no, { silent = false } = {}) => {
        const key = normalize(no);
        if (!key) return;
        const requestId = ++requestRef.current;
        const stale = () => requestId !== requestRef.current;
        queryNoRef.current = key;
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            let data = null;
            let firstError = null;
            try {
                data = await queryOrder(key);
            } catch (err) {
                firstError = err;
            }
            if (stale()) return;
            if (data) {
                setOrder(data);
                setLegacy(null);
                setError('');
                if (!silent) {
                    rememberOrder({
                        no: key,
                        uid: data.uid,
                        title: data.paperTitle,
                        kind: 'WRITING',
                        queryType: data.queryType,
                        status: data.purchaseMode && data.purchaseMode !== 'UNPAID' ? 'PAID' : 'UNPAID',
                    });
                    setRecent(rememberedOrders());
                }
                return;
            }
            // 静默刷新时不回退老接口，保留当前结果
            if (silent) return;
            try {
                const old = await queryLegacyOrder(key);
                if (stale()) return;
                setLegacy(old);
                setOrder(null);
            } catch (err) {
                if (stale()) return;
                setOrder(null);
                setLegacy(null);
                setError(errorText(firstError || err, '查询失败，请稍后重试'));
            }
        } finally {
            if (!silent && !stale()) setLoading(false);
        }
    }, []);

    // 链接带 ?no= 时直接查询；付款成功跳转、补差价后也走这里
    useEffect(() => {
        if (!urlNo) return;
        setInput(urlNo);
        runQuery(urlNo);
    }, [urlNo, runQuery]);

    const submit = (value = input) => {
        const key = normalize(value);
        if (!key) {
            setError('请输入订单号、支付交易号或已使用的兑换码');
            return;
        }
        setInput(key);
        if (key === urlNo) runQuery(key);
        else setParams({ no: key });
    };

    const docs = Array.isArray(order?.documents) ? order.documents : [];
    const paid = !!order && order.purchaseMode && order.purchaseMode !== 'UNPAID';
    const generating =
        paid &&
        (ACTIVE_STATUSES.includes(order.generationStatus) ||
            docs.some((doc) => doc.status === 'GENERATING'));

    useEffect(() => {
        if (!generating) return undefined;
        const timer = setInterval(() => {
            if (queryNoRef.current) runQuery(queryNoRef.current, { silent: true });
        }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [generating, runQuery]);

    const recentList = useMemo(() => {
        const seen = new Set();
        return recent
            .filter((item) => {
                const key = item.uid || item.no;
                if (!item.no || seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .slice(0, 10);
    }, [recent]);

    const docNo = order?.orderNo || queryNoRef.current;

    const handleDownload = async (doc) => {
        if (downloading || !doc.downloadUrl) return;
        setDownloading(doc.docKey);
        try {
            await exportDocument(doc.downloadUrl, doc.title || order?.paperTitle);
        } catch (err) {
            message.error(err?.message || '下载失败，请稍后重试');
        } finally {
            setDownloading('');
        }
    };

    const openPreview = (doc) => {
        const search = new URLSearchParams({ no: docNo, doc: doc.docKey || 'main' });
        navigate(`/order/preview?${search.toString()}`);
    };

    // 正文在整篇解锁且全部生成完成后可在线编辑：编辑器按订单号（MLW…）打开，后端首次打开时生成编辑文档
    const canEditOnline = (doc) =>
        doc.kind === 'MAIN' &&
        doc.status === 'DONE' &&
        !!order?.orderNo &&
        FULL_UNLOCK.includes(order.purchaseMode) &&
        order.generationStatus === 'COMPLETE';

    const openEditor = () => {
        window.location.href = `/editor-v2?orderNo=${encodeURIComponent(order.orderNo)}`;
    };

    const handlePaid = () => {
        if (order) {
            rememberOrder({
                no: order.orderNo || queryNoRef.current,
                uid: order.uid,
                title: order.paperTitle,
                kind: 'WRITING',
                queryType: order.queryType,
                status: 'PAID',
            });
        }
        setRecent(rememberedOrders());
        setPayMode(null);
        runQuery(queryNoRef.current);
    };

    const generation = order
        ? paid
            ? GENERATION[order.generationStatus] || { text: '处理中', tone: 'active' }
            : { text: '待支付', tone: 'muted' }
        : null;
    const progress = Math.max(0, Math.min(100, Math.round(Number(order?.progress) || 0)));

    const renderDoc = (doc) => {
        const isPpt = doc.kind === 'PPT_CODE';
        const ready = doc.status === 'DONE';
        const failed = doc.status === 'FAILED';
        const docProgress = Number(doc.progress);
        return (
            <div key={doc.docKey || doc.title} className={styles.doc}>
                <div className={styles.docInfo}>
                    <span className={styles.docKind} data-kind={doc.kind}>
                        {KIND_TEXT[doc.kind] || '文档'}
                    </span>
                    <span className={styles.docTitle}>{doc.title || '文档'}</span>
                    {!isPpt && doc.status ? (
                        <span className={styles.docStatus} data-status={doc.status}>
                            {DOC_STATUS[doc.status] || doc.status}
                            {doc.status === 'GENERATING' && Number.isFinite(docProgress) && docProgress > 0
                                ? ` ${Math.round(docProgress)}%`
                                : ''}
                        </span>
                    ) : null}
                </div>
                {isPpt ? (
                    <div className={styles.docActions}>
                        {doc.redeemCode ? (
                            <span className={styles.code}>
                                <span className={styles.mono}>{doc.redeemCode}</span>
                                <button
                                    type="button"
                                    className={styles.copyBtn}
                                    aria-label="复制答辩PPT兑换码"
                                    onClick={() => copyText(doc.redeemCode, message)}
                                >
                                    <CopyOutlined />
                                </button>
                            </span>
                        ) : null}
                        <Button
                            type="primary"
                            icon={<Presentation size={14} />}
                            disabled={!doc.redeemCode}
                            onClick={() => navigate(`/ppt?code=${encodeURIComponent(doc.redeemCode)}`)}
                        >
                            {doc.redeemCode ? '去制作答辩PPT' : '兑换码生成中'}
                        </Button>
                    </div>
                ) : (
                    <div className={styles.docActions}>
                        <Button
                            icon={<EyeOutlined />}
                            disabled={doc.previewable === false || failed}
                            onClick={() => openPreview(doc)}
                        >
                            {ready ? '预览' : '查看进度'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!ready || !doc.downloadUrl}
                            loading={downloading === doc.docKey}
                            onClick={() => handleDownload(doc)}
                        >
                            下载 Word
                        </Button>
                        {canEditOnline(doc) ? (
                            <Button icon={<EditOutlined />} onClick={openEditor}>
                                在线编辑
                            </Button>
                        ) : null}
                    </div>
                )}
            </div>
        );
    };

    const renderOrder = () => (
        <section className={styles.orderCard} aria-live="polite">
            <div className={styles.orderHead}>
                <div className={styles.orderTitleWrap}>
                    <h2 className={styles.orderTitle}>{order.paperTitle || '写作订单'}</h2>
                    <StatusPill tone={generation.tone}>{order.statusText || generation.text}</StatusPill>
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => runQuery(queryNoRef.current)}
                >
                    刷新
                </Button>
            </div>

            {paid && order.generationStatus !== 'COMPLETE' ? (
                <div className={styles.progress}>
                    <Progress percent={progress} showInfo={false} strokeColor="var(--ant-color-primary)" />
                    <span>
                        已完成 {progress}%{generating ? '，页面每 10 秒自动刷新' : ''}
                    </span>
                </div>
            ) : null}

            {!paid ? (
                <div className={styles.callout}>
                    <span>订单尚未支付，付款后自动开始生成全文。</span>
                    <Button type="primary" icon={<UnlockOutlined />} onClick={() => setPayMode('unlock')}>
                        去支付
                    </Button>
                </div>
            ) : null}

            {order.canUpgrade ? (
                <div className={styles.callout}>
                    <span>当前为单章解锁订单，补差价即可解锁并生成全文。</span>
                    <Button type="primary" icon={<UnlockOutlined />} onClick={() => setPayMode('upgrade')}>
                        补差价解锁全文
                        {order.upgradePrice != null ? ` ¥${formatYuan(order.upgradePrice)}` : ''}
                    </Button>
                </div>
            ) : null}

            <dl className={styles.meta}>
                <div>
                    <dt>订单号</dt>
                    <dd>
                        <span className={styles.mono}>{order.orderNo || queryNoRef.current}</span>
                        <button
                            type="button"
                            className={styles.copyBtn}
                            aria-label="复制订单号"
                            onClick={() => copyText(order.orderNo || queryNoRef.current, message)}
                        >
                            <CopyOutlined />
                        </button>
                    </dd>
                </div>
                <div>
                    <dt>类型</dt>
                    <dd>{QUERY_TYPE_LABEL[order.queryType] || '写作'}</dd>
                </div>
                <div>
                    <dt>购买方式</dt>
                    <dd>{PURCHASE_TEXT[order.purchaseMode] || '-'}</dd>
                </div>
                <div>
                    <dt>创建时间</dt>
                    <dd>{order.createTime || '-'}</dd>
                </div>
            </dl>

            <div className={styles.docs}>
                {docs.length === 0 ? (
                    <div className={styles.docEmpty}>
                        {paid ? '文档准备中，请稍候…' : '付款后在这里查看、预览和下载文档'}
                    </div>
                ) : (
                    docs.map(renderDoc)
                )}
            </div>
        </section>
    );

    const renderLegacy = () => {
        const done = legacy.list.filter((file) => file.downloadUrl).length;
        return (
            <section className={styles.orderCard} aria-live="polite">
                <div className={styles.orderHead}>
                    <div className={styles.orderTitleWrap}>
                        <h2 className={styles.orderTitle}>{legacy.paperTitle || '我的生成文件'}</h2>
                        <StatusPill tone={legacy.list.length && done === legacy.list.length ? 'done' : 'active'}>
                            已生成 {done} / {legacy.list.length} 个文件
                        </StatusPill>
                    </div>
                    <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => runQuery(queryNoRef.current)}
                    >
                        刷新
                    </Button>
                </div>
                <div className={styles.docs}>
                    {legacy.list.length === 0 ? (
                        <div className={styles.docEmpty}>文件生成中，请稍后刷新</div>
                    ) : (
                        legacy.list.map((file, index) => (
                            <div key={`${file.title}-${index}`} className={styles.doc}>
                                <div className={styles.docInfo}>
                                    <span className={styles.docKind}>文件</span>
                                    <span className={styles.docTitle}>{file.title || `文件 ${index + 1}`}</span>
                                </div>
                                <div className={styles.docActions}>
                                    {file.downloadUrl ? (
                                        <a
                                            className={styles.linkBtn}
                                            href={file.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <DownloadOutlined /> 下载文件
                                        </a>
                                    ) : (
                                        <span className={styles.docPending}>生成中，请稍候</span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <p className={styles.legacyHint}>
                    这是旧版订单；编辑、解锁全文等操作请在顶部「订单与下载」中进行。
                </p>
            </section>
        );
    };

    return (
        <div className={styles.page}>
            <main className={styles.container}>
                <header className={styles.hero}>
                    <h1 className={styles.heroTitle}>
                        订单<span className={styles.heroAccent}>与下载</span>
                    </h1>
                    <p className={styles.heroDesc}>
                        免登录订单凭订单号、支付宝 / 微信交易号或已使用的兑换码，即可查看进度、预览和下载
                    </p>
                </header>

                <section className={styles.searchCard}>
                    <form
                        className={styles.searchRow}
                        onSubmit={(e) => {
                            e.preventDefault();
                            submit();
                        }}
                    >
                        <Input
                            size="large"
                            allowClear
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="订单号 / 支付交易号 / 兑换码"
                            aria-label="订单号、交易号或兑换码"
                            prefix={<SearchOutlined className={styles.searchIcon} />}
                            maxLength={64}
                        />
                        <Button
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            className={styles.searchBtn}
                        >
                            查询
                        </Button>
                    </form>
                    <p className={styles.searchHint}>
                        订单号在付款弹窗里；也可以在支付宝 / 微信账单详情里找到「商户单号」或「交易号」
                    </p>
                    {error ? (
                        <div className={styles.error} role="alert">
                            {error}
                        </div>
                    ) : null}
                </section>

                {order ? renderOrder() : null}
                {!order && legacy ? renderLegacy() : null}

                {recentList.length > 0 ? (
                    <section className={styles.recentCard}>
                        <h3 className={styles.sectionTitle}>本机最近的订单</h3>
                        <ul className={styles.recentList}>
                            {recentList.map((item) => (
                                <li key={item.no}>
                                    <button
                                        type="button"
                                        className={styles.recentItem}
                                        onClick={() => submit(item.no)}
                                    >
                                        <span className={styles.recentTitle}>{item.title || '写作订单'}</span>
                                        <span className={styles.mono}>{item.no}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                <section className={styles.notesCard}>
                    <h3 className={styles.sectionTitle}>订单说明</h3>
                    <ul className={styles.notes}>
                        <li>免登录订单不绑定账号，凭订单号、支付交易号或兑换码访问；拿到订单号的人都能查看内容，请妥善保管。</li>
                        <li>全文生成过程中可以先预览已完成的部分，生成完成后即可下载 Word。</li>
                        <li>购买了答辩PPT附加服务的订单会生成兑换码，点「去制作答辩PPT」直接使用。</li>
                        <li>长时间没有进展或遇到问题，请联系页面右侧客服并提供订单号。</li>
                    </ul>
                </section>
            </main>

            {order ? (
                <WritingPayModal
                    open={!!payMode}
                    onClose={() => setPayMode(null)}
                    uid={order.uid}
                    title={order.paperTitle}
                    queryType={order.queryType}
                    mode={payMode === 'upgrade' ? 'upgrade' : 'unlock'}
                    upgradePrice={order.upgradePrice}
                    onPaid={handlePaid}
                />
            ) : null}
        </div>
    );
}
